import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiMail, FiPhone, FiMapPin, FiBriefcase, FiUser, FiSave, FiX, FiShare2, FiBellOff } from 'react-icons/fi';
import { getClient, updateClient, getJobs, getClientPortalLink } from '../services/api';
import ClientContacts from '../components/ClientContacts';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [clientJobs, setClientJobs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClient();
    loadJobs();
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await getClient(id);
      setClient(response.data);
      setFormData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading client:', error);
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      // Ask the server for just this client's jobs. Fetching the whole job list and
      // filtering here meant serialising every job in the system to show a handful.
      const response = await getJobs(`client_id=${encodeURIComponent(id)}`);
      setClientJobs(response.data.jobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const shareClientPortal = async () => {
    try {
      const res = await getClientPortalLink(id);
      const url = res.data.url;
      if (navigator.share) {
        await navigator.share({ title: `${client?.name || 'Client'} — Projects`, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Client portal link copied:\n\n' + url + '\n\nThey can view all their projects here (verified by last 4 digits of their phone).');
      }
    } catch (e) {
      if (e.name !== 'AbortError') alert('Could not get the portal link.');
    }
  };

  const handleSave = async () => {
    try {
      await updateClient(id, formData);
      setClient(formData);
      setEditing(false);
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
    }
  };

  const handleCancel = () => {
    setFormData(client);
    setEditing(false);
  };

  // Format full address for display
  const formatAddress = (c) => {
    const parts = [];
    if (c.address) parts.push(c.address);
    const cityStateZip = [
      c.city,
      c.state,
      c.zip_code
    ].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join(', ');
  };

  if (loading) {
    return <div className="loading">Loading client...</div>;
  }

  if (!client) {
    return <div className="error">Client not found</div>;
  }

  return (
    <div className="client-detail-page">
      <button className="btn btn-ghost" onClick={() => navigate('/clients')}>
        <FiArrowLeft /> Back to Clients
      </button>

      <div className="detail-header">
        <div>
          {client.company_name ? (
            <>
              <h1>{client.company_name}</h1>
              <p style={{ color: '#666', fontSize: '16px', margin: '4px 0 0' }}>
                <FiUser style={{ marginRight: '6px' }} />
                {client.name}
              </p>
            </>
          ) : (
            <h1>{client.name}</h1>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {editing ? (
            <>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <FiX /> Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                <FiSave /> Save
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={shareClientPortal} title="Copy the client's portal link (all their projects, protected by last 4 of phone)">
                <FiShare2 /> Portal Link
              </button>
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                <FiEdit /> Edit
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-content">
        {editing ? (
          <div className="edit-form" style={{ 
            backgroundColor: 'white', 
            padding: '24px', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="form-control"
                  placeholder="ABC Construction LLC"
                />
              </div>
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="form-control"
                  required
                />
              </div>
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="form-control"
              />
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.state || ''}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>ZIP</label>
                <input
                  type="text"
                  value={formData.zip_code || ''}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows="4"
                className="form-control"
              />
            </div>

            {/* Do-not-contact switches. Turn these off when a client asks to stop
                being notified — they suppress status-change alerts, en route, and
                the Monday digest. They do NOT block someone deliberately using
                "Email to Client"; that's a person choosing to send. */}
            <div className="form-group" style={{
              border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginTop: 8,
            }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Automatic notifications
              </label>
              <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 10px' }}>
                Turn off if this client has asked us to stop contacting them. Applies to
                job status updates, en route alerts, and the weekly project digest.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.notify_email !== false}
                  onChange={(e) => setFormData({ ...formData, notify_email: e.target.checked })}
                />
                <span>Send automatic emails</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.notify_sms !== false}
                  onChange={(e) => setFormData({ ...formData, notify_sms: e.target.checked })}
                />
                <span>Send automatic text messages</span>
              </label>
            </div>
            <div className="form-group" style={{
              border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginTop: 8,
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={!!formData.is_engineering_firm}
                  onChange={(e) => setFormData({ ...formData, is_engineering_firm: e.target.checked })}
                />
                <span>Engineering firm</span>
              </label>
              <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>
                Jobs for engineers are site-design work, so they need a Georgia 811 ticket by default.
              </p>
            </div>
          </div>
        ) : (
          <div className="client-info-grid" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            {client.company_name && (
              <div className="info-item">
                <FiBriefcase style={{ color: '#1976d2', fontSize: '20px' }} />
                <div>
                  <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Company</label>
                  <p style={{ fontWeight: '500', margin: '4px 0 0' }}>{client.company_name}</p>
                </div>
              </div>
            )}
            <div className="info-item">
              <FiUser style={{ color: '#1976d2', fontSize: '20px' }} />
              <div>
                <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Contact</label>
                <p style={{ fontWeight: '500', margin: '4px 0 0' }}>{client.name}</p>
              </div>
            </div>
            {client.email && (
              <div className="info-item">
                <FiMail style={{ color: '#1976d2', fontSize: '20px' }} />
                <div>
                  <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Email</label>
                  <p style={{ margin: '4px 0 0' }}>
                    <a href={`mailto:${client.email}`} style={{ color: '#1976d2' }}>{client.email}</a>
                  </p>
                </div>
              </div>
            )}
            {client.phone && (
              <div className="info-item">
                <FiPhone style={{ color: '#1976d2', fontSize: '20px' }} />
                <div>
                  <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Phone</label>
                  <p style={{ margin: '4px 0 0' }}>
                    <a href={`tel:${client.phone}`} style={{ color: '#1976d2' }}>{client.phone}</a>
                  </p>
                </div>
              </div>
            )}
            {formatAddress(client) && (
              <div className="info-item" style={{ gridColumn: 'span 2' }}>
                <FiMapPin style={{ color: '#1976d2', fontSize: '20px' }} />
                <div>
                  <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Address</label>
                  <p style={{ margin: '4px 0 0' }}>{formatAddress(client)}</p>
                </div>
              </div>
            )}
            {/* Only shown when something is switched OFF — a client on the normal
                settings needs no callout, but an opted-out one must be obvious so
                nobody wonders why they stopped getting updates. */}
            {(client.notify_email === false || client.notify_sms === false) && (
              <div className="info-item full-width" style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
                  padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <FiBellOff style={{ color: '#b45309', fontSize: 18, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#92400e', fontSize: 14 }}>
                      Automatic notifications are off
                    </div>
                    <div style={{ color: '#92400e', fontSize: 13 }}>
                      {client.notify_email === false && client.notify_sms === false
                        ? 'This client receives no automatic emails or texts.'
                        : client.notify_email === false
                          ? 'No automatic emails (texts still send).'
                          : 'No automatic texts (emails still send).'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {client.is_engineering_firm && (
              <div className="info-item full-width" style={{ gridColumn: '1 / -1' }}>
                <span style={{
                  display: 'inline-block', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                  background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                }}>Engineering firm · jobs need Georgia 811 by default</span>
              </div>
            )}
            {client.notes && (
              <div className="info-item full-width" style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <div>
                  <label style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Notes</label>
                  <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{client.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ClientContacts
        clientId={id}
        companyLabel={client.company_name || client.name}
      />

      <div className="client-jobs" style={{ marginTop: '24px' }}>
        <h2>Jobs for {client.company_name || client.name}</h2>
        {clientJobs.length > 0 ? (
          <div className="jobs-list" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '16px',
            marginTop: '16px'
          }}>
            {clientJobs.map(job => (
              <Link 
                key={job.id} 
                to={`/jobs/${job.id}`} 
                className="job-item"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>{job.job_number}</h3>
                  {job.description && (
                    <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
                      {job.description.substring(0, 50)}{job.description.length > 50 ? '...' : ''}
                    </p>
                  )}
                </div>
                <span className={`status-badge status-${job.status?.toLowerCase().replace(' ', '-')}`}>
                  {job.status}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <p style={{ color: '#666', marginTop: '16px' }}>No jobs for this client yet</p>
        )}
      </div>
    </div>
  );
}

export default ClientDetail;